import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SECTION_ORDER = [
  "will", "emotions", "intellect", "environment", "behavior",
  "desires_aversions", "sleep", "physical_generals", "modalities",
  "etiology", "concomitant", "central_constitutional_state",
];
const SECTION_TITLE = {
  will: "1. Will (Volition)",
  emotions: "2. Emotions (Affections)",
  intellect: "3. Intellect",
  environment: "4. Reaction to Environment",
  behavior: "5. Behavior & Personality",
  desires_aversions: "6. Desires & Aversions",
  sleep: "7. Sleep (Mental Expression)",
  physical_generals: "8. Physical Generals",
  modalities: "9. Modalities as Mental Reactions",
  etiology: "10. Etiology (Mental Cause)",
  concomitant: "11. Concomitant Mental State",
  central_constitutional_state: "12. Central Constitutional State",
};
const GRADE_COLOR = {
  PROMINENT: "#D4AF37",
  MODERATE: "#A8896C",
  AFFLICTED: "#C15C3D",
  ABSENT: "#6E6B64",
};

export default function KentianView({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.kentian(profileId)); }
    catch (e) {
      console.error("kentian failed:", e);
      toast.error(e?.response?.data?.detail || "Failed to load Kentian view");
    }
    setLoading(false);
  }, [profileId]);
  const refresh = async () => {
    setRefreshing(true);
    try { setData(await api.refreshKentian(profileId)); toast.success("Refreshed"); }
    catch (err) { console.error("refresh kentian failed:", err); toast.error("Refresh failed"); }
    setRefreshing(false);
  };
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Distilling Kentian layers… (~60s first time)</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Kentian Teja View</div>
          <div className="text-[10px] text-[#6E6B64] font-mono mt-1">Every physical symptom → mental root · 12-level Kent hierarchy</div>
        </div>
        <Button data-testid="refresh-kentian-btn" onClick={refresh} disabled={refreshing}
                variant="ghost" className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent">
          <RefreshCw size={14} className={refreshing ? "animate-spin mr-2" : "mr-2"} /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTION_ORDER.map((key) => {
          const sec = data[key] || {};
          const isCCS = key === "central_constitutional_state";
          return (
            <div key={key} className="border border-[#2A2D35] bg-[#181A1F] p-5" data-testid={`kentian-${key}`}>
              <div className="font-heading text-lg text-[#D4AF37]">{SECTION_TITLE[key]}</div>
              <p className="mt-2 text-sm text-[#E6E1D6] font-light leading-relaxed">{sec.summary || ""}</p>
              {isCCS ? (
                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8896C]">Dominant Pattern</div>
                    <div className="text-[#E6E1D6]">{sec.dominant_pattern}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8896C]">Simillimum Direction</div>
                    <div className="text-[#E6E1D6]">{sec.simillimum_direction}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8896C]">Governing Signature</div>
                    <div className="text-[#E6E1D6]">{sec.governing_signature}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  {Object.entries(sec.factors || {}).map(([factor, val]) => (
                    <div key={factor} className="flex items-start gap-2 text-xs">
                      <span className="font-mono w-[70px] shrink-0 uppercase tracking-wider"
                            style={{ color: GRADE_COLOR[val?.grade] || "#6E6B64" }}>
                        {val?.grade || "—"}
                      </span>
                      <div className="flex-1">
                        <span className="text-[#E6E1D6] font-medium">{factor}</span>
                        <span className="text-[#A19E95] font-light"> — {val?.reading}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
